import React, { useRef, useState } from 'react';
import { Icon, message } from 'antd';
import * as S from './styles';
import { useTranslation } from 'react-i18next';
import { BATCH_JOBS_DASHBOARD as trans } from './translationValues';
import moment from 'moment';

// Import split components
import Step1BatchInfo from './components/Step1BatchInfo';
import Step2TemplateSelection from './components/Step2TemplateSelection';
import Step3DataUpload from './components/Step3DataUpload';
import Step3FormFilling from './components/Step3FormFilling';
import Step4ProofBatch from './components/Step4ProofBatch';
import Step5Scheduling from './components/Step5Scheduling';
import { BatchJobProvider, useBatchJob } from '../../../../context/BatchJobContext';
import { uploadDataSetTransaction } from '../../../../utils/services/documentAPI';
import { uploadComplexEmailDataSet } from '../../../../utils/services/complexEmailAPI';
import { uploadEnvDataSet } from '../../../../utils/services/documentAPI';
import { saveBatchInfo, createAdvancedBatch, updateBatchInfo, proceedProof, batchSchedule } from '../../../../utils/services/statusMonitoringAPI';

const NAMESPACE = ['BATCH_JOBS_DASHBOARD'];

function CreateBatchContent(props) {
  const {
    currentStep,
    jobType,
    format,
    steps,
    totalSteps,
    canProceed,
    handleNext,
    handleBack,
    handleSubmit,
    setCurrentPage,
    onReturn,
    requestData,
    jobName,
    envValue,
    setCurrentStep,
    batchId,
    setBatchId,
    selectedLanguage,
    selectedTemplate,
    proffDataList,
    setProffDataList,
    totalProofCount,
    setTotalProofCount,
    scheduleDate,
    scheduleTime,
    selectedApprover,
    scheduleEnabled,
    approvalEnabled,
    approvalType,
    approvalLevel,
    uploadedFile,
    draftBatchInfo,
    initialFetchRef,
    setFilterValues,
    setFilters,
    setRequiresMyApproval,
    autoTrigger
  } = useBatchJob();

  const isDraft = draftBatchInfo !== undefined;

  const { t } = useTranslation(NAMESPACE);

  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  // ref to immediately block repeated submissions before state updates take effect
  const loadingRef = useRef(false);
  const [batchMetadata, setBatchMetadata] = useState(null);

  const submitForm = async () => {
    if (formRef.current) {
      setIsSubmitting(true);
      await formRef.current.submitForm();
      setIsSubmitting(false);
    }
  };

  const handleCreate = async () => {
    setIsSubmitting(true);
    const successMessage = t(trans.SUCCESS_CREATE_JOB.key, trans.SUCCESS_CREATE_JOB.value);
    if (!requestData) {
      message.error(t(trans.DATA_INVALID.key, trans.DATA_INVALID.value));
      return;
    }
    let response;
    try{
      if (format === 'PRINT') {
        response = await uploadDataSetTransaction(jobName, requestData, false, true);
      } else if (format === 'EMAIL') {
        response = await uploadComplexEmailDataSet(jobName, requestData, true);
      } else if (format === 'MULTI CHANNEL') {
        response = await uploadEnvDataSet(envValue, requestData, jobName, true);
      }

      if (response?.statusCode === 200) {
        message.success(successMessage);
        setTimeout(() => {
          setCurrentPage('dashboard');
        }, 1000);
      } else {
        message.error(t(trans.DATA_INVALID.key, trans.DATA_INVALID.value));
      }
      setIsSubmitting(false);
    }
    catch (error){
      console.error(error)
    }
  };

  const finishUpload = async()=>{
    const payload={
      batchName: jobName,
      batchId: batchId,
      channelType: format.toLowerCase().includes('multi') ? 'multiChannel': format.toLowerCase(),
      communicationId: selectedTemplate,
      requests: requestData,
      metadata: batchMetadata,
      fileName: uploadedFile.name,
      stage: 3,
    }
    try{
      let response = await createAdvancedBatch(payload);
      return response;
    }
    catch(error) {
      console.error(error);
      return null;
    }
  }
  
  const handleSave = async () => {
    let payload;
    let response;
    const channelType = format.toLowerCase().includes('multi') ? 'multiChannel' : format.toLowerCase();
    const { draftBatchId, batchName, channelType: draftChannelType } = draftBatchInfo ?? {};
    setLoading(true);
    loadingRef.current = true;
    try {
      if (
        currentStep === 1 &&
        (!isDraft || (isDraft && (batchName !== jobName || draftChannelType !== channelType)))
      ) {
        payload = {
          batchName: jobName,
          batchType: jobType,
          channelType,
          ...(((!isDraft && initialFetchRef.current) ||
            (isDraft && draftChannelType !== channelType)) && { stage: 1 }),
          batchId: batchId || draftBatchId,
        };
        response = await saveBatchInfo(payload);
        if (response && response?.statusCode === 7031) {
          setBatchId(response?.batchId);
        }
      } else if(currentStep === 2 || currentStep === 4) {
        try {
          await updateBatchInfo({
            batchId,
            stage: currentStep,
          });
        } catch (e) {
          message.error(e);
        }
      } else if (currentStep === 3) {
        const uploadResponse = await finishUpload();
        if (uploadResponse?.data?.statusCode !== 7035) {
          message.error(t(trans.DATA_INVALID.key, trans.DATA_INVALID.value));
          return;
        }
        payload = {
          search: [
            {
              fieldName: 'stage',
              value: 'yet_to_start'
            },
            {
              fieldName: 'channel',
              value: format.includes('MULTI') ? 'print' : format.toLowerCase()
            }
          ],
          jobId: String(batchId)
        }
        response = await proceedProof(payload);
        setProffDataList(response?.data?.proofs);
        setTotalProofCount({ print: response?.data?.printCount, email: response?.data?.emailCount });
      }
      setCurrentStep(currentStep + 1);
    }
    catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }

  const handleScheduleBatch = async () => {
    // prevent multiple invocations before state updates
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    let payload = {
      batchId: batchId,
      processingType: autoTrigger ? 'IMMEDIATE' : 'MANUAL',
      approvalRequired: false,
    };

    if (scheduleEnabled) {
      if (!scheduleDate || !scheduleTime) {
        message.error(t(trans.SCHEDULE_DATE_TIME_REQUIRED.key, trans.SCHEDULE_DATE_TIME_REQUIRED.value));
        // reset loading so user can try again after fixing
        setLoading(false);
        loadingRef.current = false;
        return;
      }
    }

    if (approvalEnabled && approvalType === 'USER') {
      if (!selectedApprover) {
        message.error(t(trans.APPROVER_REQUIRED.key, trans.APPROVER_REQUIRED.value));
        setLoading(false);
        loadingRef.current = false;
        return;
      }
    }

    if (approvalEnabled && approvalType === 'LEVEL') {
      if (!approvalLevel) {
        message.error(t(trans.LEVEL_REQUIRED.key, trans.LEVEL_REQUIRED.value));
        setLoading(false);
        loadingRef.current = false;
        return;
      }
    }


    if (scheduleTime && scheduleDate && scheduleEnabled) {
      const utcDateTime = moment(`${scheduleDate} ${scheduleTime}`, "YYYY-MM-DD HH:mm:ss")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      payload = {
        ...payload,
        processingType: 'SCHEDULED',
        scheduledTime: utcDateTime,
      }
    }
    
    if (approvalEnabled && (approvalType === 'USER' || approvalType === 'LEVEL')) {
      payload = {
        ...payload,
        approvalRequired: true,
        approvalType,
        approver: approvalType === 'USER' ? selectedApprover : approvalLevel,
      };
    }

    try {
      const res = await batchSchedule(payload);
      if (res?.data && res?.data.statusCode === 7004) {
        message.success(scheduleEnabled ? t(trans.SUCCESS_SCHEDULE_JOB.key, trans.SUCCESS_SCHEDULE_JOB.value) : t(trans.SUCCESS_CREATE_JOB.key, trans.SUCCESS_CREATE_JOB.value));
        setTimeout(() => {
          props.setDraftBatchInfo(undefined);
          setCurrentPage('dashboard');
        }, 1000);
      }
      else {
        message.error(t(trans.FAILED_SCHEDULE_JOB.key, trans.FAILED_SCHEDULE_JOB.value));
      }
    }
    catch {
      //handle
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }

  const handleAdvancedBatch = async() => {
    // protect against rapid double-clicks (schedule handler also guards)
    if (loadingRef.current) return;

    if([1, 2, 3, 4].includes(currentStep)) {
      await handleSave();
      // handleNext();
  }
  else if(currentStep === 5){
    await handleScheduleBatch();
  }
  }
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <Step1BatchInfo />;
      case 2:
        return <Step2TemplateSelection />;
      case 3:
        return jobType === 'FORM' ? <Step3FormFilling ref={formRef} /> : <Step3DataUpload setBatchMetadata={setBatchMetadata} />;
      case 4:
        return <Step4ProofBatch />;
      case 5:
        return <Step5Scheduling />;
      default:
        return null;
    }
  };

  return (
    <S.PageContainer>
      <S.Header>
        <S.HeaderFlex>
          <div>
            <S.Title>
              {t(trans.CREATE_NEW_BATCH_TITLE.key, trans.CREATE_NEW_BATCH_TITLE.value)}
            </S.Title>
            <S.Subtitle>
              {t(trans.CREATE_NEW_BATCH_SUBTITLE.key, trans.CREATE_NEW_BATCH_SUBTITLE.value)}
            </S.Subtitle>
          </div>
          <S.ReturnButton onClick={() => { 
            props.setDraftBatchInfo(undefined);
            setFilterValues({});
            const startDate = moment()
              .subtract(7, 'day')
              .startOf('day');
            const endDate = moment().endOf('day');
            const formattedStartDate = startDate.format('YYYY-MM-DDTHH:mm:ss');
            const formattedEndDate = endDate.format('YYYY-MM-DDTHH:mm:ss');
            setFilters({filter: { page: 1, startDate: formattedStartDate, endDate: formattedEndDate , limit: 4},});
            setRequiresMyApproval(false);
            setCurrentPage('dashboard');
            
          }}>
            <Icon type="left" />
            {t(trans.RETURN_TO_DASHBOARD.key, trans.RETURN_TO_DASHBOARD.value)}
          </S.ReturnButton>
        </S.HeaderFlex>
      </S.Header>

      <S.MainLayout>
        <S.SidebarWrapper>
          <S.Sidebar>
            <S.StepList>
              {steps.map((step, index) => (
                <div key={step.number}>
                  <S.StepItem>
                    <S.StepNumber
                      active={currentStep === step.number}
                      completed={currentStep > step.number}
                    >
                      {currentStep > step.number ? <Icon type="check" /> : step.number}
                    </S.StepNumber>
                    <S.StepInfo>
                      <S.StepTitle active={currentStep >= step.number}>{step.title}</S.StepTitle>
                      <S.StepDescription active={currentStep >= step.number}>
                        {step.description}
                      </S.StepDescription>
                    </S.StepInfo>
                  </S.StepItem>
                  {index < steps.length - 1 && (
                    <S.StepLineWrapper>
                      <S.StepLine completed={currentStep > step.number} />
                    </S.StepLineWrapper>
                  )}
                </div>
              ))}
            </S.StepList>
          </S.Sidebar>
        </S.SidebarWrapper>

        <S.ContentArea>
          <S.ContentCard>
            <S.StepWrapper>{renderStepContent()}</S.StepWrapper>

            <S.NavFooter>
              <S.NavButton onClick={handleBack} disabled={currentStep === 1 || currentStep === 4 || loading}>
                <Icon type="left" /> {t(trans.BACK.key, trans.BACK.value)}
              </S.NavButton>
              <div style={{ display: 'flex', gap: '8px' }}>
                {steps.map(s => (
                  <S.ProgressDot key={s.number} active={currentStep >= s.number} />
                ))}
              </div>
              {jobType === 'ADVANCED' ? ( 
                <S.NavButton primary onClick={handleAdvancedBatch} disabled={!canProceed() || loading}>
                  {loading && <Icon type="loading" />}
                  {currentStep === 1 ? t(trans.SAVE_N_PROCEED_BUTTON.key, trans.SAVE_N_PROCEED_BUTTON.value) :
                  currentStep === 2 ? t(trans.PROCEED_BUTTON.key, trans.PROCEED_BUTTON.value) :
                  currentStep === 3 ? t(trans.PROCEED_TO_PROOFING_BUTTON.key, trans.PROCEED_TO_PROOFING_BUTTON.value) :
                  currentStep === 4 ? t(trans.NEXT_BUTTON.key, trans.NEXT_BUTTON.value) : 
                  currentStep === 5 ? t(trans.SUBMIT_JOB_BUTTON.key, trans.SUBMIT_JOB_BUTTON.value) : ''}<Icon type="right" />
                </S.NavButton>
                ) :
              currentStep < totalSteps ? (
                <S.NavButton primary onClick={handleNext} disabled={!canProceed()}>
                  {t(trans.NEXT.key, trans.NEXT.value)} <Icon type="right" />
                </S.NavButton>
              ) : (
                <S.NavButton
                  primary
                  onClick={() => (jobType === 'FORM' ? submitForm() : handleCreate())}
                  disabled={!canProceed() || isSubmitting}
                >
                  {isSubmitting ? <Icon type="loading" /> : <Icon type="check" />}{' '}
                  {jobType === 'FORM'
                    ? format === 'PRINT'
                      ? t(trans.DOWNLOAD.key, trans.DOWNLOAD.value)
                      : format === 'CALL'
                      ? t(trans.INITIATE_CALL.key, trans.INITIATE_CALL.value)
                      : t(trans.SEND.key, trans.SEND.value)
                    : isSubmitting
                    ? t(trans.CREATING_BATCH.key, trans.CREATING_BATCH.value)
                    : t(trans.CREATE_BATCH_BUTTON.key, trans.CREATE_BATCH_BUTTON.value)}
                </S.NavButton>
              )}
            </S.NavFooter>
          </S.ContentCard>
        </S.ContentArea>
      </S.MainLayout>
    </S.PageContainer>
  );
}

export default function CreateBatch(props) {
  return (
    <BatchJobProvider {...props}>
      <CreateBatchContent setDraftBatchInfo={props.setDraftBatchInfo} />
    </BatchJobProvider>
  );
}
